






















import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { EVENT_NAMES } from "@scrile/api-provider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { Transaction } from "@scrile/api-provider/dist/api/TransactionProvider";
import { isScrileWebView } from "@scrile/tools/dist/lib/browserUtils";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import { useCreditCardAssignment } from "@/hooks/useCreditCardAssignment";
import { usePostMessage } from "@/hooks/usePostMessage";
import useSystemSettings from "@/hooks/useSystemSettings";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getMainCurrencyCode } from "@/lib/moneyHelpers";
import CallsLogger from "@/modules/callsLogger";
import getIntegration from "@/payments/integrations";
import { PAYMENT_ERROR_MESSAGES } from "@/payments/integrations/utils";
import { OnStartPurchaseData, PurchaseResponse, SubscriptionResponse } from "@/payments/types";
import config from "@/config";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView";
import ChoosePaymentSystem from "./components/ChoosePaymentSystem";
import PaymentCancellationConfirm from "./components/PaymentCancellationConfirm";

export default defineComponent({
  name: "PurchaseManager",
  components: { PaymentCancellationConfirm, ChoosePaymentSystem, ModalView },
  setup() {
    const { publicPaymentSystems } = useSystemSettings();
    const { getCreditCardsBySystemId } = useCreditCardAssignment();
    const { sendMessage, getAsyncMessage } = usePostMessage<any>(window, config.webviewTargetOrigin);

    const show = ref(false);
    const showCloseConfirm = ref(false);
    const processingRequest = ref(false);
    const pricePackageId = ref("");
    const paymentSystemId = ref<string | null>(null);
    const price = ref("");
    const purchaseSubject = ref<Subject<PurchaseSubjectTypeEnum> | null>(null);
    const subscriptionSubject = ref<Subject<RecurringBillingScheduleSubjectTypeEnum> | null>(null);
    const closeOnError = ref(false);

    const currentPaymentSystem = computed(
      () => publicPaymentSystems.value.find(i => i.id === paymentSystemId.value) ?? null
    );
    const integrationModule = computed(() => getIntegration(currentPaymentSystem.value));
    const forceShowUI = computed(() => integrationModule.value?.forceShowUI ?? false);
    const allowedSaveCard = computed(() => integrationModule.value?.allowSaveCard ?? false);

    const closeModal = (payload: PurchaseResponse | SubscriptionResponse) => {
      emitter.emit(!subscriptionSubject.value ? EVENTS.PAYMENT_PURCHASED : EVENTS.PAYMENT_SUBSCRIBED, payload);
      pricePackageId.value = "";
      price.value = "";
      show.value = false;
      paymentSystemId.value = null;
      subscriptionSubject.value = null;
      closeOnError.value = false;
      showCloseConfirm.value = false;
    };

    const onClose = () => {
      if (processingRequest.value && !showCloseConfirm.value) {
        showCloseConfirm.value = true;
        return;
      }
      closeModal({
        status: false,
        subjectPurchased: false,
      });
    };

    const startPurchase = async (data?: any) => {
      try {
        processingRequest.value = true;
        if (!pricePackageId.value || !currentPaymentSystem.value) {
          throw new Error("Cant find payment information");
        }
        let payload: any;
        if (subscriptionSubject.value) {
          if (!integrationModule.value.subscribe) {
            throw new Error(`${integrationModule.value.name} is not support recurring payments`);
          }
          payload = await integrationModule.value.subscribe(
            {
              currency: getMainCurrencyCode(),
              paymentSystemId: currentPaymentSystem.value.id,
              subject: subscriptionSubject.value,
            },
            data
          );
        } else {
          if (!integrationModule.value.purchase) {
            throw new Error(`${integrationModule.value.name} is not support one-time payments`);
          }
          payload = await integrationModule.value.purchase(
            {
              currency: getMainCurrencyCode(),
              paymentSystemId: currentPaymentSystem.value.id,
              pricePackageId: pricePackageId.value,
            },
            data
          );
        }
        closeModal(payload);
      } finally {
        processingRequest.value = false;
      }
    };

    const onSubmit = (data?: any) =>
      startPurchase(data).catch(e => {
        if (e.error?.message === PAYMENT_ERROR_MESSAGES.OTHER_PAYMENT) {
          setTimeout(() =>
            emitter.emit(EVENT_NAMES.REQUEST_STATUS, {
              status: "error",
              message: $t("Other payment in process"),
            })
          );
        } else {
          // eslint-disable-next-line no-console
          console.error(e);
        }
        if (closeOnError.value) {
          closeModal({
            status: false,
            subjectPurchased: false,
            error: e,
          });
        }
      });

    const startPurchaseIOSWebView = async () => {
      try {
        if (!pricePackageId.value) throw new Error("Cant find payment information");
        await CallsLogger.collectLogs("Start ios WebView purchase", {
          pricePackageId: pricePackageId.value,
          streaming: false,
        });
        let transaction: Transaction | undefined,
          receipt = "",
          status = false;

        await sendMessage({ data: { price: price.value, pricePackageId: pricePackageId.value } });
        const messageData = await getAsyncMessage();
        await CallsLogger.collectLogs("Received payment data", { data: messageData, streaming: false });

        try {
          const data = JSON.parse(messageData);
          status = data.status;
          receipt = data.receipt;
        } catch (e) {
          await CallsLogger.collectLogs("Payment ios WebView error", { data: messageData, error: e, streaming: false });
        }

        if (status)
          transaction = await providers.PurchaseProvider.purchaseCreateAppleStore({
            data: { receipt, subject: purchaseSubject.value ?? undefined },
          });

        closeModal({
          status: !!transaction,
          subjectPurchased: !!purchaseSubject.value && !!transaction,
          transaction,
        });
      } finally {
        await CallsLogger.collectLogs("Finished ios WebView purchase", {
          pricePackageId: pricePackageId.value,
          streaming: false,
        });
        processingRequest.value = false;
      }
    };

    const onStartPurchase = useAuthorisedOnly(async (data?: OnStartPurchaseData) => {
      try {
        if (!data) {
          throw new Error("Purchase data wasn't provided");
        }
        price.value = data?.price ?? "";
        pricePackageId.value = data.pricePackageId;
        paymentSystemId.value = data.paymentSystemId;
        subscriptionSubject.value = data.subscriptionSubject ?? null;
        purchaseSubject.value = data.purchaseSubject ?? null;
        if (isScrileWebView() && !subscriptionSubject.value) {
          await startPurchaseIOSWebView();
          return;
        }
        if (!forceShowUI.value && allowedSaveCard.value) {
          const cardsAssignmentData = getCreditCardsBySystemId(paymentSystemId.value);
          if (cardsAssignmentData) {
            await startPurchase();
            return;
          }
        }
        show.value = true;
      } catch (e) {
        console.error(e);
        closeModal({ status: false, subjectPurchased: false, error: e });
      }
    });
    emitter.on<OnStartPurchaseData>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);

    onBeforeUnmount(() => {
      emitter.off<OnStartPurchaseData>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);
    });

    return {
      show,
      processingRequest,
      integrationModule,
      price,
      currentPaymentSystem,
      closeOnError,
      pricePackageId,
      purchaseSubject,
      subscriptionSubject,
      publicPaymentSystems,
      showCloseConfirm,
      onSubmit,
      onClose,
    };
  },
});
